import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import * as Yup from "yup";
import Loader from "../../../../components/Loader";
import { set } from "react-hook-form";
import {
  formatDateInTimezone,
  getCurrentDate,
} from "../../../../utils/formatDateInTimezone";
import ListGoal from "./ListGoal";
import createCustomSchema from "./../../../../fieldValidator/fieldValidator";
import "../../../../assets/css/createGoal.css";
import { ErrorBar } from "recharts";
import AcknowledgmentPopup from "./../../../../utils/AcknowledgmentPopup";
import { companyID, DepartmentID } from "../../../../auth";
import PerformanceAppraisalCard from "../Performance/PerformanceAppraisalCard";
import api from "../../../../api/axios";
// import { getCurrentDate } from './../../../../utils/formatDateInTimezone';

const CreateGoal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get("employee_id");
  const year = searchParams.get("year");
  const company_id = companyID();

  const emp_id = localStorage.getItem("employeeID");
  const location = useLocation();
  const {
    hasGoals,
    reviewer,
    back,
    goal_review_deadline,
    isReviewed,
    goal_creation_review,
    reportee_submission_status,
    level,
    permission,
    performance_appraisal,
    companyId,
    cycle_id,
    edited,
    creatingNewGoal,
    reporter_deadline,
  } = location.state || {};

  useEffect(() => {
    if (!employeeId && !year) {
      navigate("../goal-list");
    }
  }, []);

  const goalData = {
    mainTitle: null,
    startDate: null,
    endDate: null,
    main_description: null,
    weightage: null,
    comment: null,
    contribution_level: null,
    subGoals: [
      {
        subTitle: null,
        description: null,
      },
    ],
  };

  // const navigate = useNavigate();
  const [formValues, setFormValues] = useState([goalData]);
  const [errors, setErrors] = useState({});
  const [totalGoalError, setTotalGoalError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Validation Schema
  const subGoalSchema = Yup.object({
    subTitle: Yup.string()
      .required("Sub Goal is required")
      .min(3, "At least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "At least 10 characters"),
  });

  const mainGoalSchema = Yup.object({
    mainTitle: Yup.string()
      .required("Main Goal is required")
      .min(5, "At least 5 characters"),
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date().required("End Date is required"),
    // .min( "End Date cannot be before Start Date"),
    main_description: Yup.string()
      .required("Description is required")
      .min(10, "At least 10 characters"),
    weightage: Yup.number()
      .nullable() // Allows null values
      .required("Weightage is required")
      .min(0.01, "Weightage must be at least 0.01%")
      .max(100, "Weightage cannot exceed 100%"),
    subGoals: Yup.array()
      .of(subGoalSchema)
      .min(1, "At least one sub-goal is required"),
    contribution_level: createCustomSchema("dropdown", {
      message: "Please Select a Contribution Level!",
    }).when("$isSubmit", {
      is: false, // When not in submission mode, validation is required
      then: (schema) => schema.required("Please Select a Contribution Level!"),
      otherwise: (schema) => schema.notRequired(),
    }),
    comment: Yup.string()
      .min(10, "At least 10 characters")
      .when("$isSubmit", {
        is: false, // When not in submission mode, validation is required
        then: (schema) => schema.required("Comment is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const validationSchema = Yup.array().of(mainGoalSchema);

  // Calculate total weightage
  const totalWeightage = formValues.reduce(
    (acc, goal) => acc + parseFloat(goal?.weightage || 0),
    0
  );
  const remainingWeightage = Math.max(
    0,
    parseFloat((100 - totalWeightage).toFixed(2))
  );

  const handleChange = async (index, mainIndex, e) => {
    setTotalGoalError(null);
    const { name, value } = e.target;

    // Deep copy of formValues for immutability
    const updatedFormValues = [...formValues];

    if (
      [
        "mainTitle",
        "startDate",
        "endDate",
        "main_description",
        "weightage",
        "comment",
        "contribution_level",
      ].includes(name)
    ) {
      // Directly update the value without trimming spaces for live typing
      updatedFormValues[mainIndex][name] = value;
      setFormValues(updatedFormValues);

      // Validate only after user finishes typing (onBlur or form submission)
      try {
        const trimmedValue = value.trim() ? value.trim() : null; // Trim value before validation
        await mainGoalSchema.validateAt(name, {
          ...updatedFormValues[mainIndex],
          [name]: trimmedValue,
        });
        setErrors((prevErrors) => ({
          ...prevErrors,
          [mainIndex]: {
            ...prevErrors[mainIndex],
            [name]: null,
          },
        }));

        if (totalWeightage > 100) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [mainIndex]: {
              ...prevErrors[mainIndex],
              [name]: "Total weightage cannot exceed 100%",
            },
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [mainIndex]: {
              ...prevErrors[mainIndex],
              [name]: null,
            },
          }));
        }
        if (
          name === "startDate" &&
          value > updatedFormValues[mainIndex].endDate
        ) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [mainIndex]: {
              ...prevErrors[mainIndex],
              startDate: "Start date cannot be after the end date",
              endDate: null,
            },
          }));
        } else if (
          name === "endDate" &&
          value < updatedFormValues[mainIndex].startDate
        ) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [mainIndex]: {
              ...prevErrors[mainIndex],
              endDate: "End date cannot be earlier than the start date",
              startDate: null,
            },
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [mainIndex]: {
              ...prevErrors[mainIndex],
              [name]: null,
            },
          }));
        }
      } catch (validationError) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [mainIndex]: {
            ...prevErrors[mainIndex],
            [name]: validationError.message,
          },
        }));
      }
    } else {
      // Handle sub-goal fields
      updatedFormValues[mainIndex].subGoals[index][name] = value;
      setFormValues(updatedFormValues);

      // Validate only after user finishes typing
      try {
        const trimmedValue = value.trim(); // Trim value before validation
        await subGoalSchema.validateAt(name, {
          ...updatedFormValues[mainIndex].subGoals[index],
          [name]: trimmedValue,
        });
        setErrors((prevErrors) => ({
          ...prevErrors,
          [mainIndex]: {
            ...prevErrors[mainIndex],
            subGoals: {
              ...prevErrors[mainIndex]?.subGoals,
              [index]: {
                ...prevErrors[mainIndex]?.subGoals?.[index],
                [name]: null,
              },
            },
          },
        }));
      } catch (validationError) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [mainIndex]: {
            ...prevErrors[mainIndex],
            subGoals: {
              ...prevErrors[mainIndex]?.subGoals,
              [index]: {
                ...prevErrors[mainIndex]?.subGoals?.[index],
                [name]: validationError.message,
              },
            },
          },
        }));
      }
    }
  };

  const handleAddMainGoal = () => {
    setFormValues([
      ...formValues,
      {
        mainTitle: null,
        startDate: null,
        endDate: null,
        main_description: null,
        weightage: null,
        contribution_level: null,
        comment: null,
        subGoals: [
          {
            subTitle: null,
            description: null,
          },
        ],
      },
    ]);
  };

  const handleAddSubGoal = (mainIndex) => {
    const updatedFormValues = [...formValues];
    updatedFormValues[mainIndex].subGoals.push({
      subTitle: null,
      description: null,
    });
    setFormValues(updatedFormValues);
  };

  const handleRemoveMainGoal = async (mainIndex, goal) => {
    if (goal?.main_goal_id) {
      const response = await api.post(
        `performance/update-mainGoal-status`,
        goal
      );
    }
    const updatedFormValues = [...formValues];
    updatedFormValues.splice(mainIndex, 1);
    setFormValues(updatedFormValues);
  };

  const handleRemoveSubGoal = (mainIndex, subGoalIndex) => {
    // Create a new array to avoid mutating the original state
    const updatedFormValues = [...formValues];

    // Remove the specific subgoal
    updatedFormValues[mainIndex].subGoals.splice(subGoalIndex, 1);

    // Update the state with the modified array
    setFormValues(updatedFormValues);
  };

  const validate = async (filteredFormValues, state) => {
    const validationErrors = {}; // Initialize an empty object to store errors

    try {
      // Perform async validation
      await validationSchema.validate(filteredFormValues, {
        abortEarly: false,
        context: { isSubmit: state },
      });
    } catch (err) {
      err?.inner?.forEach((e) => {
        if (e.path) {
          // Split the path for nested fields
          const pathParts = e.path.split(".");
          let currentLevel = validationErrors;

          pathParts.forEach((part, index) => {
            if (part.startsWith("[") && part.endsWith("]")) {
              const arrayIndex = part.slice(1, -1); // Extract index from "[0]"
              if (!currentLevel[arrayIndex]) {
                currentLevel[arrayIndex] = {}; // Initialize the object
              }
              currentLevel = currentLevel[arrayIndex];
            } else if (part.includes("subGoals")) {
              // Handle subGoals as an array
              if (!currentLevel.subGoals) {
                currentLevel.subGoals = []; // Initialize subGoals array
              }
              const subIndexMatch = part.match(/\d+/); // Extract subGoal index
              if (subIndexMatch) {
                const subIndex = subIndexMatch[0];
                if (!currentLevel.subGoals[subIndex]) {
                  currentLevel.subGoals[subIndex] = {}; // Initialize the subGoal object
                }
                currentLevel = currentLevel.subGoals[subIndex];
              }
            } else {
              // Regular fields
              if (index === pathParts.length - 1) {
                // Set the error message for the last part
                currentLevel[part] = e.message;
              } else {
                if (!currentLevel[part]) {
                  currentLevel[part] = {}; // Initialize the object
                }
                currentLevel = currentLevel[part];
              }
            }
          });
        }
      });
    }

    // Transform subGoals into the desired structure
    Object.keys(validationErrors).forEach((key) => {
      if (Array.isArray(validationErrors[key]?.subGoals)) {
        const formattedSubGoals = {};
        validationErrors[key].subGoals.forEach((subGoal, index) => {
          formattedSubGoals[index] = subGoal;
        });
        validationErrors[key].subGoals = formattedSubGoals;
      }
    });

    setErrors(validationErrors); // Set the errors in state
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const company_id = companyID();

    const validationErrors = await validate(formValues, true);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (totalWeightage !== 100) {
      setTotalGoalError("Your total goal weightage must be exactly 100%");
      window.scrollTo(0, 0);
      return;
    }

    setIsLoading(true);

    try {
      const id = localStorage.getItem("employeeID");
      const data = {
        goals: formValues,
        employee_id: employeeId ? employeeId : id,
        year: year ? year : currentYear,
      };
      console.log("data :", data);
      if (hasGoals == true && employeeId && year) {
        const response = await api.post(
          `performance/update-goal?reviewer=${id}&company_id=${company_id}&cycle_id=${cycle_id}`,
          data
        );
        if (response?.data?.success == true) {
          navigate("../goal-list");
        }
        console.log(response);
      } else {
        const response = await api.post(
          `performance/create-goal?reviewer=${id}&company_id=${company_id}&cycle_id=${cycle_id}`,
          data
        );
        console.log("response create goal", response);
        if (response?.data?.success == true) {
          navigate("../goal-list");
        }
      }
    } catch (err) {
      console.error("Error submitting form:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const [currentYear, setCurrentYear] = useState(null);

  useEffect(() => {
    const date = new Date();
    setCurrentYear(date?.getFullYear());
  }, []);

  //--------------------------------- Edit Goal -----------------------------

  const [goalEmployeeDetails, setGoalEmployeeDetails] = useState([]);
  const listAllGaolForEmployee = async (employeeID, year) => {
    setIsLoading(true);
    const ID = localStorage.getItem("employeeID");
    console.log("ID", ID);
    const company_id = companyID();
    const response = await api.get(
      `performance/goals?employee_id=${employeeID}&year=${year}&reviewer=${ID}&company_id=${company_id}&cycle_id=${cycle_id}`
    );
    console.log(response);
    if (response?.data?.result?.length > 0) {
      if (creatingNewGoal == true) {
        setFormValues([]);
      } else {
        console.log("(response?.data?.result", response?.data?.result);
        setIsLoading(false);
        setFormValues(response?.data?.result);
      }
    }
    if (response?.data?.employeeDetails?.length > 0) {
      if (creatingNewGoal == true) {
        setGoalEmployeeDetails([]);
        setIsLoading(false);
      } else {
        setGoalEmployeeDetails(response?.data?.employeeDetails);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      setFormValues([]);
    }
  };

  const [toggleEdit, setToggleEdit] = useState(false);
  const [openIndexing, setOpenIndexing] = useState(null);
  const [allContributionLevels, setAllContributionLevels] = useState([]);
  const toggling = (edit_data) => {
    setOpenIndexing(edit_data?.main_goal_id);
    console.log("edit_data", edit_data);
    setToggleEdit(true);
  };
  const listContributionLevel = async () => {
    const user = localStorage.getItem("user");
    console.log("listContributionLevel", company_id);
    try {
      if (company_id || companyId) {
        const response = await api.get(
          `performance/contribution-level/${company_id || companyId}`
        );
        console.log("contrivution level", response);
        if (response?.data?.result?.length > 0) {
          setAllContributionLevels(response?.data?.result);
        }
      }
    } catch (err) {
      console.log("list goal error", err);
    }
  };
  useEffect(() => {
    if (hasGoals == true && employeeId && year) {
      listAllGaolForEmployee(employeeId, year);
      setToggleEdit(false);
      listContributionLevel();
    }
  }, [hasGoals]);

  useEffect(() => {
    listContributionLevel();
  }, []);

  const [acknowledgmentPopup, setAcknowledgmentPopup] = useState({
    showAcknowledgmentPopup: false,
    popupVariant: "",
    popupMessage: "",
    popupIcon: "",
  });

  const hideAcknowledgmentPopup = () => {
    const resetAcknowledgmentPopupData = () => {
      setAcknowledgmentPopup({
        showAcknowledgmentPopup: false,
        popupVariant: "",
        popupMessage: "",
        popupIcon: "",
      });
    };
    window.scrollTo(0, 0);
    navigate(`..${back}`);
    setTimeout(() => resetAcknowledgmentPopupData(), 2000);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    const company_id = companyID();
    console.log("formValues reviews", formValues);
    const validationErrors = await validate(formValues, false);
    console.log("validationErrors", validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    const id = localStorage.getItem("employeeID");
    const data = {
      reviews: formValues,
      reviewed_by: id,
      year: year,
      goal_employee_id: formValues[0]?.employee_id,
    };
    console.log("data", data);
    window.scrollTo(0, 0);
    try {
      setIsLoading(true);
      const department_id = DepartmentID();
      const response = await api.post(
        `performance/self-review?reviewer=${reviewer}&company_id=${company_id}&department_id=${department_id}&level=${level}&cycle_id=${cycle_id}&year=${year}`,
        data
      );
      console.log("response slef review", response);
      if (response?.data?.success == true) {
        setAcknowledgmentPopup({
          showAcknowledgmentPopup: true,
          popupVariant: "success",
          popupMessage:
            "Goal Performance Review Request Submitted Successfully!",
          popupIcon: "fa-regular fa-circle-check",
        });
      }
      setIsLoading(false);
      hideAcknowledgmentPopup();
      console.log("response self reviewss", response);
    } catch (err) {
      setAcknowledgmentPopup({
        showAcknowledgmentPopup: true,
        popupVariant: "danger",
        popupMessage: err?.response?.data?.message,
        popupIcon: "fa-regular fa-circle-xmark",
      });
      setIsLoading(false);
      hideAcknowledgmentPopup();
    }
  };

  const [selfGoalReviewCycle, setSelfGoalReviewCycle] = useState(null);
  const [goalReviewSubmission, setGoalReviewSubmission] = useState({});
  const [goalEditSubmission, setGoalEditSubmission] = useState(null);
  const [performanceReview, setPerformanceReview] = useState(null);
  const [createGoal, setCreateGoal] = useState(null);

  const fetchConfiguration = async () => {
    const company_id = companyID();
    const current_date = getCurrentDate();
    const response = await api.get(
      `performance/goal-config?company_id=${company_id}&self_review_deadline=${current_date}&cycle_id=${cycle_id}`
    );
    if (response?.data?.result?.length > 0) {
      setGoalReviewSubmission(response?.data?.result[0]);
    } else {
      setGoalReviewSubmission({});
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, []);
  //----------------------- handling Edit Access ---------------------------------

  const handleEditAccess = async (e, goal_employee_id, reportee_id) => {
    e.preventDefault();
    setIsLoading(true);
    const company_id = companyID();
    try {
      const response = await api.post(
        `performance/allow-edit-goal?goal_employee_id=${goal_employee_id}&reportee_id=${reportee_id}&year=${year}&company_id=${company_id}&cycle_id=${cycle_id}`,
        formValues
      );
      if (response?.data?.success == true) {
        navigate(`..${back}`);
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };
  const handleApprovedGoal = async (e, goal_employee_id, reportee_id) => {
    e.preventDefault();
    setIsLoading(true);
    const department_id = DepartmentID();
    const company_id = companyID();
    try {
      const response = await api.post(
        `performance/approved-goal?goal_employee_id=${goal_employee_id}&reportee_id=${reportee_id}&year=${year}&level=${level}&department_id=${department_id}&company_id=${company_id}&cycle_id=${cycle_id}`,
        formValues
      );
      if (response?.data?.success == true) {
        navigate(`..${back}`);
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  const navigateBack = () => {
    navigate(`..${back}`, {
      state: {
        backState: true,
        employeeId: employeeId,
        year: year,
        emp_first_name:
          formValues[0]?.goal_employee_first_name ||
          goalEmployeeDetails[0]?.first_name,
        emp_last_name:
          formValues[0]?.goal_employee_last_name ||
          goalEmployeeDetails[0]?.last_name,
        emp_middle_name:
          formValues[0]?.goal_employee_middle_name ||
          goalEmployeeDetails[0]?.middle_name,
      },
    });
  };

  const isReviewPeriodEnded =
    reviewer &&
    reporter_deadline &&
    getCurrentDate() > formatDateInTimezone(reporter_deadline) &&
    isReviewed === 0;

  const isGoalReviewClosed =
    formValues[0]?.submission_status_id === 2 &&
    getCurrentDate() > goal_review_deadline;

  return (
    <>
      {isLoading && <Loader show={isLoading} />}
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle={
              reviewer == false
                ? employeeId && year && hasGoals == true
                  ? `View Goal ${year && `For ${year}`}`
                  : `Create Goal ${
                      year ? year : currentYear && `For ${currentYear}`
                    } `
                : `Performance Assesment  ${currentYear} ${
                    (formValues || goalEmployeeDetails?.length > 0) &&
                    `For ${
                      formValues[0]?.goal_employee_first_name
                        ? formValues[0]?.goal_employee_first_name
                        : goalEmployeeDetails[0]?.first_name
                    } ${
                      formValues[0]?.goal_employee_middle_name == null ||
                      formValues[0]?.goal_employee_middle_name == `null`
                        ? " "
                        : formValues[0]?.goal_employee_middle_name
                        ? formValues[0]?.goal_employee_middle_name
                        : goalEmployeeDetails[0]?.middle_name
                        ? goalEmployeeDetails[0]?.middle_name
                        : ""
                    } ${
                      formValues[0]?.goal_employee_last_name
                        ? formValues[0]?.goal_employee_last_name
                        : goalEmployeeDetails[0]?.last_name
                    }`
                  }`
            }
            title="Goal List"
            subtitle={
              employeeId && year && hasGoals == true
                ? "View Goal"
                : "Create Goal"
            }
            titleLink="goal-list"
            modal="#add_goal"
            name="Add New"
          />

          <div className="d-flex justify-content-between">
            <div>
              <h4>Total Goal Weightage : {totalWeightage?.toFixed(2)}%</h4>
              {totalGoalError && (
                <p style={{ color: "#ff3333" }}>*{totalGoalError}</p>
              )}
            </div>
            {/* <Link to={`..${back}`}> */}
            <span
              onClick={navigateBack}
              className="text-primary btn-link"
              style={{ cursor: "pointer" }}
            >
              Goal List
            </span>
            {/* </Link> */}
          </div>
          {getCurrentDate() >
            formatDateInTimezone(formValues[0]?.edit_deadline) &&
            formValues[0]?.self_reviewed != 1 &&
            formValues[0]?.employee_id == emp_id && (
              <span className="text-danger ">
                {" "}
                *Goal review closed. Reach out to HR if you need help.{" "}
              </span>
            )}
          {(isGoalReviewClosed || isReviewPeriodEnded) && (
            <span className="text-danger">
              {isGoalReviewClosed
                ? "* Goal review closed. For concerns, please reach out to HR."
                : "* Review period has ended. For assistance, contact HR."}
            </span>
          )}

          {formValues[0]?.submission_status_id == 1 &&
            getCurrentDate() >
              formatDateInTimezone(formValues[0]?.edit_deadline) &&
            getCurrentDate() <
              formatDateInTimezone(
                goalReviewSubmission?.self_review_deadline
              ) && (
              <span className="text-danger">
                * Deadline passed. Goal can’t be modified now.
              </span>
            )}
          <Container
            className="my-2 create-goal-wrapper"
            style={{ padding: "0px" }}
          >
            <Form>
              {(employeeId && year && hasGoals == true
                ? formValues[0]?.main_goal_id
                : formValues.length > 0) &&
                formValues?.map((goal, mainIndex) =>
                  openIndexing == goal?.main_goal_id || edited ? (
                    <Card key={mainIndex} style={{ boxShadow: "none" }}>
                      <Card.Body>
                        <Row>
                          <div
                            className=" d-flex  justify-content-between"
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex ">
                              <h4>
                                {" "}
                                {goal?.mainTitle && <b>1.{mainIndex + 1} - </b>}
                                <span style={{ marginLeft: "4px" }}>
                                  {goal?.mainTitle}
                                </span>
                              </h4>
                            </div>

                            <div>
                              {formValues.length !== 1 && (
                                <i
                                  className="fa-solid fa-trash"
                                  style={{ color: "#de2121" }}
                                  onClick={() =>
                                    handleRemoveMainGoal(mainIndex, goal)
                                  }
                                ></i>
                              )}
                            </div>
                          </div>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group
                              controlId={`mainTitle-${mainIndex}`}
                              className="mb-3"
                            >
                              <Form.Label>Goal</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter main title"
                                name="mainTitle"
                                value={goal?.mainTitle}
                                onChange={(e) => handleChange(0, mainIndex, e)}
                                isInvalid={errors[mainIndex]?.mainTitle}
                              />
                              {errors[mainIndex]?.mainTitle && (
                                <Form.Control.Feedback type="invalid">
                                  {errors[mainIndex]?.mainTitle}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group
                              controlId={`weightage-${mainIndex}`}
                              className="mb-3"
                            >
                              <Form.Label>Weightage (%)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                placeholder="Enter weightage"
                                name="weightage"
                                value={goal.weightage}
                                onChange={(e) => handleChange(0, mainIndex, e)}
                                isInvalid={errors[mainIndex]?.weightage}
                              />
                              {errors[mainIndex]?.weightage && (
                                <Form.Control.Feedback type="invalid">
                                  {errors[mainIndex]?.weightage}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group
                              controlId={`startDate-${mainIndex}`}
                              className="mb-3"
                            >
                              <Form.Label>Start Date</Form.Label>
                              <Form.Control
                                type="date"
                                name="startDate"
                                value={
                                  goal.startDate
                                    ? formatDateInTimezone(goal.startDate)
                                    : null
                                }
                                onChange={(e) => handleChange(0, mainIndex, e)}
                                min={`${new Date().getFullYear()}-01-01`}
                                max={`${new Date().getFullYear()}-12-31`}
                                isInvalid={errors[mainIndex]?.startDate}
                              />
                              {errors[mainIndex]?.startDate && (
                                <Form.Control.Feedback type="invalid">
                                  {errors[mainIndex]?.startDate}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group
                              controlId={`endDate-${mainIndex}`}
                              className="mb-3"
                            >
                              <Form.Label>End Date</Form.Label>
                              <Form.Control
                                type="date"
                                name="endDate"
                                value={
                                  goal.endDate
                                    ? formatDateInTimezone(goal.endDate)
                                    : null
                                }
                                min={`${new Date().getFullYear()}-01-01`}
                                max={`${new Date().getFullYear()}-12-31`}
                                onChange={(e) => handleChange(0, mainIndex, e)}
                                isInvalid={errors[mainIndex]?.endDate}
                              />
                              {errors[mainIndex]?.endDate && (
                                <Form.Control.Feedback type="invalid">
                                  {errors[mainIndex]?.endDate}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group
                              controlId={`main_description-${mainIndex}`}
                              className="mb-3"
                            >
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Enter description"
                                name="main_description"
                                value={goal.main_description}
                                onChange={(e) => handleChange(0, mainIndex, e)}
                                isInvalid={errors[mainIndex]?.main_description}
                              />
                              {errors[mainIndex]?.main_description && (
                                <Form.Control.Feedback type="invalid">
                                  {errors[mainIndex]?.main_description}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        {goal.subGoals.map((subgoal, index) => (
                          <Card
                            key={index}
                            style={{
                              boxShadow:
                                "0px 4px 24px 0px rgba(188, 188, 188, 0.2509803922)",
                            }}
                          >
                            <Card.Body>
                              <Row>
                                <div
                                  className=" d-flex  justify-content-between"
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="d-flex ">
                                    <h4>
                                      {" "}
                                      {subgoal?.subTitle && (
                                        <>
                                          <b>
                                            1.{mainIndex + 1}.{index + 1}{" "}
                                          </b>{" "}
                                          <span style={{ marginLeft: "4px" }}>
                                            - {subgoal?.subTitle}
                                          </span>
                                        </>
                                      )}
                                    </h4>
                                  </div>

                                  <div>
                                    {goal?.subGoals?.length !== 1 && (
                                      <i
                                        className="fa-solid fa-trash"
                                        style={{ color: "#de2121" }}
                                        onClick={() =>
                                          handleRemoveSubGoal(mainIndex, index)
                                        }
                                      ></i>
                                    )}
                                  </div>
                                </div>
                              </Row>

                              <Row>
                                <Col md={12}>
                                  <Form.Group
                                    controlId={`subTitle-${index}-${mainIndex}`}
                                    className="mb-3"
                                  >
                                    <Form.Label>Sub Goal</Form.Label>
                                    <Form.Control
                                      type="text"
                                      placeholder="Enter sub title"
                                      name="subTitle"
                                      value={subgoal?.subTitle || null}
                                      onChange={(e) =>
                                        handleChange(index, mainIndex, e)
                                      }
                                      isInvalid={
                                        errors[mainIndex]?.subGoals?.[index]
                                          ?.subTitle
                                      }
                                    />
                                    {errors[mainIndex]?.subGoals?.[index]
                                      ?.subTitle && (
                                      <Form.Control.Feedback type="invalid">
                                        {
                                          errors[mainIndex]?.subGoals?.[index]
                                            ?.subTitle
                                        }
                                      </Form.Control.Feedback>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>

                              <Row>
                                <Col md={12}>
                                  <Form.Group
                                    controlId={`description-${index}-${mainIndex}`}
                                    className="mb-3"
                                  >
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={2}
                                      placeholder="Enter description"
                                      name="description"
                                      value={subgoal?.description || null}
                                      onChange={(e) =>
                                        handleChange(index, mainIndex, e)
                                      }
                                      isInvalid={
                                        errors[mainIndex]?.subGoals?.[index]
                                          ?.description
                                      }
                                    />
                                    {errors[mainIndex]?.subGoals?.[index]
                                      ?.description && (
                                      <Form.Control.Feedback type="invalid">
                                        {
                                          errors[mainIndex]?.subGoals?.[index]
                                            ?.description
                                        }
                                      </Form.Control.Feedback>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Row>
                                {goal?.subGoals?.length - 1 == index && (
                                  <div className="d-flex justify-content-end">
                                    <Button
                                      variant="primary"
                                      onClick={() =>
                                        handleAddSubGoal(mainIndex)
                                      }
                                    >
                                      <i class="fa-solid fa-plus"></i> Add Sub
                                      Goals
                                    </Button>
                                  </div>
                                )}
                              </Row>
                            </Card.Body>
                          </Card>
                        ))}

                        <Row>
                          {formValues?.length - 1 == mainIndex && (
                            <div className="d-flex justify-content-between">
                              <Button
                                variant="primary"
                                onClick={handleAddMainGoal}
                                disabled={remainingWeightage <= 0}
                              >
                                <i class="fa-solid fa-plus"></i> Add Main Goals
                              </Button>
                            </div>
                          )}
                        </Row>
                      </Card.Body>
                    </Card>
                  ) : (
                    <ListGoal
                      goal={goal}
                      mainIndex={mainIndex}
                      allContributionLevels={allContributionLevels}
                      toggling={toggling}
                      handleChange={handleChange}
                      reviewer={reviewer}
                      errors={errors}
                      createGoal={createGoal}
                      goalReviewSubmission={goalReviewSubmission}
                      goal_review_deadline={goal_review_deadline}
                      isReviewed={isReviewed}
                      performance_appraisal={performance_appraisal}
                      permission={permission}
                    />
                  )
                )}

              {/* Remaining Weightage */}
              <div className="mb-4">
                <strong>Remaining Weightage: {remainingWeightage}%</strong>
              </div>

              {/* Add More Sub Goals */}
              <div className="d-flex justify-content-end">
                {((reviewer == false &&
                  goalReviewSubmission?.self_review_status == 1 &&
                  formValues[0]?.main_goal_id &&
                  formatDateInTimezone(
                    goalReviewSubmission?.self_review_deadline
                  ) >= getCurrentDate()) ||
                  (formValues[0]?.self_reviewed == 1 &&
                    formValues[0]?.employee_id == emp_id)) &&
                  permission != 4 && (
                    <Button
                      variant="success"
                      type="submit"
                      disabled={formValues[0]?.hide_field == 1 ? true : false}
                      onClick={submitReview}
                    >
                      Submit Review
                    </Button>
                  )}

                {((reviewer == true &&
                  formValues[0]?.self_reviewed == 1 &&
                  formatDateInTimezone(goal_review_deadline) >=
                    getCurrentDate()) ||
                  isReviewed == 1) &&
                  !performance_appraisal &&
                  permission != 4 && (
                    <Button
                      variant="success"
                      type="submit"
                      disabled={formValues[0]?.hide_field == 1 ? true : false}
                      onClick={submitReview} // Disable if total weightage exceeds 100
                    >
                      Submit Review
                    </Button>
                  )}

                {goalReviewSubmission?.self_review_status != 1 &&
                  ((formValues[0]?.main_goal_id && employeeId == emp_id) ||
                    (employeeId == emp_id &&
                      formValues[0]?.submission_status_id == 1)) && (
                    <Button
                      variant="success"
                      type="submit"
                      style={{ marginLeft: "30px" }}
                      disabled={
                        (formValues[0]?.submission_status_id == 1 &&
                          formatDateInTimezone(formValues[0]?.edit_deadline) >=
                            getCurrentDate() &&
                          formValues[0]?.main_goal_id) ||
                        !formValues[0]?.main_goal_id
                          ? false
                          : true
                      }
                      onClick={handleSubmit} // Disable if total weightage exceeds 100
                    >
                      {formValues[0]?.submission_status_id == 1
                        ? "Resubmit Goal"
                        : "Update Goal"}
                    </Button>
                  )}

                {/* {(hasGoals == true &&
                  employeeId &&
                  year &&
                  formValues[0]?.submission_status_id == 1) && (
                    <Button
                      variant="success"
                      type="submit"
                      style={{ marginLeft: "30px" }}
                      disabled={remainingWeightage < 0}
                      onClick={handleSubmit} // Disable if total weightage exceeds 100
                    >
                      Update Goal
                    </Button>
                  )} */}
                {hasGoals == false &&
                  employeeId &&
                  year &&
                  !formValues[0]?.main_goal_id && (
                    <Button
                      variant="success"
                      type="submit"
                      style={{ marginLeft: "30px" }}
                      disabled={remainingWeightage < 0}
                      onClick={handleSubmit} // Disable if total weightage exceeds 100
                    >
                      Submit Goal
                    </Button>
                  )}

                {formValues[0]?.main_goal_id &&
                  employeeId != emp_id &&
                  goalReviewSubmission?.self_review_status != 1 &&
                  // (employeeId == emp_id && formValues[0]?.submission_status_id == 1)

                  (permission == 2 || permission == null ? (
                    <>
                      <Button
                        variant="success"
                        type="submit"
                        style={{ marginLeft: "30px" }}
                        disabled={
                          formValues[0]?.submission_status_id == 1 ||
                          reportee_submission_status == 3 ||
                          (formValues[0]?.submission_status_id == 2 &&
                            getCurrentDate() >
                              formatDateInTimezone(reporter_deadline)) ||
                          (isReviewed == 0 &&
                            reporter_deadline &&
                            getCurrentDate() >
                              formatDateInTimezone(reporter_deadline))
                            ? true
                            : false
                        }
                        onClick={(e) => handleEditAccess(e, employeeId, emp_id)} // Disable if total weightage exceeds 100
                      >
                        Edit Access
                      </Button>

                      <Button
                        variant="success"
                        type="submit"
                        style={{ marginLeft: "30px" }}
                        disabled={
                          formValues[0]?.submission_status_id == 1 ||
                          reportee_submission_status == 3 ||
                          (formValues[0]?.submission_status_id == 2 &&
                            getCurrentDate() >
                              formatDateInTimezone(reporter_deadline)) ||
                          (isReviewed == 0 &&
                            reporter_deadline &&
                            getCurrentDate() >
                              formatDateInTimezone(reporter_deadline))
                            ? true
                            : false
                        }
                        onClick={(e) =>
                          handleApprovedGoal(e, employeeId, emp_id)
                        } // Disable if total weightage exceeds 100
                      >
                        Approve Goal
                      </Button>
                    </>
                  ) : (
                    !performance_appraisal &&
                    // todo
                    (permission != 4 || reportee_submission_status == 2) && (
                      <Button
                        variant="success"
                        type="submit"
                        style={{ marginLeft: "30px" }}
                        disabled={
                          formValues[0]?.submission_status_id == 1 ||
                          reportee_submission_status == 3
                            ? true
                            : false
                        }
                        onClick={(e) =>
                          handleApprovedGoal(e, employeeId, emp_id)
                        } // Disable if total weightage exceeds 100
                      >
                        Approve Goal
                      </Button>
                    )
                  ))}
              </div>
            </Form>
          </Container>
          {performance_appraisal && (
            <PerformanceAppraisalCard data={formValues} year={year} />
          )}
        </div>
      </div>

      {acknowledgmentPopup.showAcknowledgmentPopup ? (
        <AcknowledgmentPopup
          popupVariant={acknowledgmentPopup.popupVariant}
          popupMessage={acknowledgmentPopup.popupMessage}
          popupIcon={acknowledgmentPopup.popupIcon}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default CreateGoal;
